/**
 * Client-side fee estimator — deterministic, real-time, per-line fee
 * annotations derived from Mist source code.
 *
 * Mirrors the Rust fee_engine's 38-category taxonomy exactly.
 * All costs are in USD micros (1 USD = 1,000,000 micros).
 */

import type { FeeSummary, FunctionFeeSummaryItem, FunctionFeeCategoryDetail } from '../types/fees';
import type { PrefabDefinition } from '../types/prefab';

/* ================================================================== */
/*  Fee Taxonomy — mirrors FeeCategory in fee_engine.rs               */
/* ================================================================== */

export const FEE_CATEGORIES = {
  // State operations
  StateNano:            { cost: 100,       label: 'State Read (nano)',      desc: 'Simple state read' },
  StateMicro:           { cost: 500,       label: 'State Read (micro)',     desc: 'Conditional state read' },
  StateLight:           { cost: 2_000,     label: 'State Write',            desc: 'State write / transition' },
  // Compute
  ComputeStandard:      { cost: 5_000,     label: 'Compute (standard)',     desc: 'Arithmetic / comparison' },
  ComputeMedium:        { cost: 20_000,    label: 'Compute (medium)',       desc: 'Loop / conditional logic' },
  ComputeHeavy:         { cost: 50_000,    label: 'Compute (heavy)',        desc: 'Complex math / crypto verify' },
  // Storage
  StorageWrite:         { cost: 1_000,     label: 'Storage Write',          desc: 'Persistent storage write' },
  StorageRead:          { cost: 10,        label: 'Storage Read',           desc: 'Persistent storage read' },
  // Value Transfers
  TransferSimple:       { cost: 10_000,    label: 'Transfer',               desc: 'Single A→B transfer' },
  TransferMulti:        { cost: 20_000,    label: 'Multi-party Transfer',   desc: 'Multi-party / split transfer' },
  TransferStreaming:    { cost: 30_000,    label: 'Payment Stream',         desc: 'Streaming payment' },
  // DeFi
  DexSwap:              { cost: 100_000,   label: 'DEX Swap',               desc: 'Token swap' },
  LiquidityProvide:     { cost: 50_000,    label: 'Add Liquidity',          desc: 'LP deposit' },
  LiquidityRemove:      { cost: 100_000,   label: 'Remove Liquidity',       desc: 'LP withdrawal' },
  LendingBorrow:        { cost: 100_000,   label: 'Borrow',                 desc: 'Lending borrow' },
  LendingRepay:         { cost: 50_000,    label: 'Repay',                  desc: 'Lending repay' },
  LendingLiquidate:     { cost: 200_000,   label: 'Liquidate',              desc: 'Lending liquidation' },
  // NFT
  NftMint:              { cost: 50_000,    label: 'NFT Mint',               desc: 'Mint single NFT' },
  NftBatchMint:         { cost: 50_000,    label: 'NFT Batch Mint',         desc: 'Batch mint NFTs' },
  NftTransfer:          { cost: 10_000,    label: 'NFT Transfer',           desc: 'Transfer NFT' },
  NftSale:              { cost: 50_000,    label: 'NFT Sale',               desc: 'NFT sale' },
  NftRoyalty:           { cost: 20_000,    label: 'NFT Royalty',            desc: 'Royalty distribution' },
  // Subscriptions
  SubscriptionCharge:   { cost: 50_000,    label: 'Subscription Charge',    desc: 'Recurring charge' },
  SubscriptionSetup:    { cost: 20_000,    label: 'Subscription Setup',     desc: 'Set up subscription' },
  SubscriptionCancel:   { cost: 0,         label: 'Subscription Cancel',    desc: 'Cancel subscription' },
  // System surcharges
  SystemVrf:            { cost: 10_000,    label: 'VRF Call',               desc: 'Verifiable random function' },
  SystemOracle:         { cost: 10_000,    label: 'Oracle Read',            desc: 'Oracle price feed' },
  SystemOracleHeavy:    { cost: 20_000,    label: 'Oracle Read (heavy)',    desc: 'Multi-oracle read' },
  SystemZkLight:        { cost: 20_000,    label: 'ZK Proof (light)',       desc: 'Light ZK verification' },
  SystemZkHeavy:        { cost: 50_000,    label: 'ZK Proof (heavy)',       desc: 'Heavy ZK verification' },
  SystemAiLight:        { cost: 10_000,    label: 'AI Inference (light)',   desc: 'Light AI inference' },
  SystemAiHeavy:        { cost: 100_000,   label: 'AI Inference (heavy)',   desc: 'Heavy AI inference' },
  // Deploy
  DeploySimple:         { cost: 2_000_000, label: 'Deploy (simple)',        desc: 'Simple contract deploy' },
  DeployMedium:         { cost: 4_000_000, label: 'Deploy (medium)',        desc: 'Medium contract deploy' },
  DeployComplex:        { cost: 7_000_000, label: 'Deploy (complex)',       desc: 'Complex contract deploy' },
  // Infrastructure
  InfraIntent:          { cost: 100,       label: 'Intent',                 desc: 'Intent submission' },
  InfraBatch:           { cost: 1_000,     label: 'Batch Op',               desc: 'Batch operation' },
  InfraEvent:           { cost: 200,       label: 'Event',                  desc: 'Event emission' },
  // Governance
  MetaVote:             { cost: 10_000,    label: 'Vote',                   desc: 'Governance vote' },
  MetaStake:            { cost: 20_000,    label: 'Stake',                  desc: 'Staking operation' },
  // Registration
  Registration:         { cost: 1_000_000, label: 'Module Registration',    desc: 'On-chain module registration' },
  StoragePerKb:         { cost: 10_000,    label: 'Storage (per KB)',       desc: 'Bytecode storage' },
} as const;

export type FeeCategoryKey = keyof typeof FEE_CATEGORIES;

/* ================================================================== */
/*  Per-line annotation                                               */
/* ================================================================== */

export interface LineFeeAnnotation {
  line: number;             // 1-based line number
  category: FeeCategoryKey;
  cost: number;             // USD micros
  label: string;
  functionName: string | null;
}

/* ================================================================== */
/*  Source-level fee analysis                                          */
/* ================================================================== */

/** Result of analyzing a Mist source for fees. */
export interface FeeAnalysis {
  summary: FeeSummary;
  lineAnnotations: LineFeeAnnotation[];
}

/**
 * Analyze Mist source code and return a real-time, per-line fee breakdown.
 */
export function analyzeFees(source: string, contractName: string): FeeAnalysis {
  const lines = source.split('\n');
  const annotations: LineFeeAnnotation[] = [];
  let currentFn: string | null = null;

  // Per-function accumulators
  const fnFees = new Map<string, number>();
  // Count total instructions across all functions (for deploy complexity)
  let totalInstructionCount = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const lineNum = i + 1;
    const trimmed = line.trim();

    // Skip comments and blank lines
    if (trimmed === '' || trimmed.startsWith('//') || trimmed.startsWith('/*') || trimmed.startsWith('*')) continue;

    // Track function boundaries
    const fnMatch = trimmed.match(/^function\s+(\w+)/);
    if (fnMatch) {
      currentFn = fnMatch[1];
      if (!fnFees.has(currentFn)) fnFees.set(currentFn, 0);
      // Every function has a base intent cost
      addAnno('InfraIntent', lineNum);
      continue;
    }

    // Skip lines outside functions (contract-level declarations)
    if (!currentFn) continue;

    // ── State Operations ──

    // State reads: access `state.field` without assignment
    if (/\bstate\.\w+/.test(trimmed) && !/\bstate\.\w+\s*[+\-*/?]=/.test(trimmed)) {
      addAnno('StateNano', lineNum);
    }

    // State writes: `state.field = ...`
    if (/\bstate\.\w+\s*[+\-*/?]=/.test(trimmed)) {
      addAnno('StateLight', lineNum);
    }

    // Storage read: `storage.get`, `storage.read`, `storage[`, `storage.load`
    if (/\bstorage\.(get|read|load)\b/.test(trimmed) || /\bstorage\[/.test(trimmed)) {
      addAnno('StorageRead', lineNum);
    }

    // Storage write: `storage.set`, `storage.write`, `storage.save`, `storage[...] =`
    if (/\bstorage\.(set|write|save)\b/.test(trimmed) || /\bstorage\[.*\]\s*=/.test(trimmed)) {
      addAnno('StorageWrite', lineNum);
    }

    // ── Compute ──

    // Heavy compute: crypto verify, hash, sha, keccak, blake, sqrt, pow, modexp
    if (/\b(verify_signature|crypto_verify|sha256|sha3|keccak|blake[23]|sqrt|pow|modexp|verify_proof)\b/i.test(trimmed)) {
      addAnno('ComputeHeavy', lineNum);
      totalInstructionCount++;
      continue; // Don't also count as medium/standard
    }

    // Medium compute: loops and conditional blocks with computation
    if (/^\s*(for|while|loop)\b/.test(trimmed)) {
      addAnno('ComputeMedium', lineNum);
      totalInstructionCount++;
      continue;
    }

    // Standard compute: arithmetic, comparisons, if/else, let bindings with expressions
    if (/^\s*if\b/.test(trimmed) || /^\s*else\b/.test(trimmed)) {
      addAnno('ComputeStandard', lineNum);
      totalInstructionCount++;
    }

    // Arithmetic / binary operators in expressions (only if line has actual computation)
    if (/[+\-*%]/.test(trimmed) && /\blet\b/.test(trimmed)) {
      addAnno('ComputeStandard', lineNum);
      totalInstructionCount++;
    }

    // ── Value Operations ──
    if (/^\s*hold\b/i.test(trimmed)) { addAnno('TransferSimple', lineNum); totalInstructionCount++; }
    if (/^\s*pay\b/i.test(trimmed)) { addAnno('TransferSimple', lineNum); totalInstructionCount++; }
    if (/^\s*release\b/i.test(trimmed)) { addAnno('TransferSimple', lineNum); totalInstructionCount++; }
    if (/^\s*refund\b/i.test(trimmed)) { addAnno('TransferSimple', lineNum); totalInstructionCount++; }
    if (/^\s*transfer\b/i.test(trimmed)) { addAnno('TransferSimple', lineNum); totalInstructionCount++; }
    if (/^\s*split\b/i.test(trimmed)) { addAnno('TransferMulti', lineNum); totalInstructionCount++; }
    if (/^\s*mint\b/i.test(trimmed)) { addAnno('NftMint', lineNum); totalInstructionCount++; }
    if (/^\s*burn\b/i.test(trimmed)) { addAnno('TransferSimple', lineNum); totalInstructionCount++; }

    // ── DeFi ──
    if (/^\s*swap\b/i.test(trimmed)) { addAnno('DexSwap', lineNum); totalInstructionCount++; }
    if (/\badd_liquidity\b/i.test(trimmed) || /\bprovide_liquidity\b/i.test(trimmed)) { addAnno('LiquidityProvide', lineNum); totalInstructionCount++; }
    if (/\bremove_liquidity\b/i.test(trimmed)) { addAnno('LiquidityRemove', lineNum); totalInstructionCount++; }
    if (/^\s*borrow\b/i.test(trimmed)) { addAnno('LendingBorrow', lineNum); totalInstructionCount++; }
    if (/^\s*repay\b/i.test(trimmed)) { addAnno('LendingRepay', lineNum); totalInstructionCount++; }
    if (/\bliquidate\b/i.test(trimmed)) { addAnno('LendingLiquidate', lineNum); totalInstructionCount++; }

    // ── NFT ──
    if (/\bbatch_mint\b/i.test(trimmed)) { addAnno('NftBatchMint', lineNum); totalInstructionCount++; }
    if (/\bnft_transfer\b/i.test(trimmed) || /\btransfer_nft\b/i.test(trimmed)) { addAnno('NftTransfer', lineNum); totalInstructionCount++; }
    if (/\bnft_sale\b/i.test(trimmed) || /\bsell_nft\b/i.test(trimmed) || /\blist_nft\b/i.test(trimmed)) { addAnno('NftSale', lineNum); totalInstructionCount++; }
    if (/\broyalty\b/i.test(trimmed)) { addAnno('NftRoyalty', lineNum); totalInstructionCount++; }

    // ── Subscriptions ──
    if (/\bsubscribe\b/i.test(trimmed) || /\bcharge_subscription\b/i.test(trimmed)) { addAnno('SubscriptionCharge', lineNum); totalInstructionCount++; }
    if (/\bsetup_subscription\b/i.test(trimmed) || /\bcreate_subscription\b/i.test(trimmed)) { addAnno('SubscriptionSetup', lineNum); totalInstructionCount++; }
    if (/\bcancel_subscription\b/i.test(trimmed)) { addAnno('SubscriptionCancel', lineNum); totalInstructionCount++; }

    // ── System surcharges ──
    if (/\boracle\s*\(/.test(trimmed) || /\boracle_read\b/i.test(trimmed) || /\bget_price\b/i.test(trimmed)) { addAnno('SystemOracle', lineNum); totalInstructionCount++; }
    if (/\bvrf\s*\(/.test(trimmed) || /\brandom\s*\(/.test(trimmed) || /\bvrf_random\b/i.test(trimmed)) { addAnno('SystemVrf', lineNum); totalInstructionCount++; }
    if (/\bzk_verify\b/i.test(trimmed) || /\bproof\s*\(/.test(trimmed) || /\bverify_zk\b/i.test(trimmed)) { addAnno('SystemZkLight', lineNum); totalInstructionCount++; }
    if (/\bai_infer\b/i.test(trimmed) || /\binference\s*\(/.test(trimmed)) { addAnno('SystemAiLight', lineNum); totalInstructionCount++; }

    // ── Events ──
    if (/^\s*emit\b/i.test(trimmed)) { addAnno('InfraEvent', lineNum); totalInstructionCount++; }

    // ── Governance ──
    if (/^\s*stake\b/i.test(trimmed)) { addAnno('MetaStake', lineNum); totalInstructionCount++; }
    if (/^\s*vote\b/i.test(trimmed)) { addAnno('MetaVote', lineNum); totalInstructionCount++; }

    // ── Streaming ──
    if (/^\s*stream\b/i.test(trimmed)) { addAnno('TransferStreaming', lineNum); totalInstructionCount++; }

    // ── Require / assert = compute ──
    if (/^\s*require\b/i.test(trimmed) || /^\s*assert\b/i.test(trimmed)) {
      addAnno('ComputeStandard', lineNum);
      totalInstructionCount++;
    }
  }

  function addAnno(category: FeeCategoryKey, lineNum: number) {
    const cat = FEE_CATEGORIES[category];
    annotations.push({
      line: lineNum,
      category,
      cost: cat.cost,
      label: cat.label,
      functionName: currentFn,
    });
    if (currentFn) {
      fnFees.set(currentFn, (fnFees.get(currentFn) || 0) + cat.cost);
    }
  }

  // Build per-function summary
  const functions: FunctionFeeSummaryItem[] = [];
  let maxFee = 0;
  for (const [fnName, fee] of fnFees) {
    const fnAnnotations = annotations.filter(a => a.functionName === fnName);
    // Aggregate per-category: count occurrences and total cost
    const catMap = new Map<string, { count: number; total: number }>();
    for (const a of fnAnnotations) {
      const entry = catMap.get(a.category) || { count: 0, total: 0 };
      entry.count += 1;
      entry.total += a.cost;
      catMap.set(a.category, entry);
    }
    const categories = [...catMap.entries()].map(([key, val]) => {
      const cat = FEE_CATEGORIES[key as FeeCategoryKey];
      return {
        category: key,
        label: cat.label,
        count: val.count,
        unit_cost: cat.cost,
        total_cost: val.total,
        total_display: formatUsd(val.total),
      };
    });
    functions.push({
      function_name: fnName,
      fee_micro: String(fee),
      fee_display: formatUsd(fee),
      category_count: categories.length,
      categories,
    });
    if (fee > maxFee) maxFee = fee;
  }

  // Deploy tier — mirrors Rust DeploymentComplexity::classify()
  // Simple: ≤ 3 functions, ≤ 50 instructions
  // Medium: ≤ 10 functions, ≤ 200 instructions
  // Complex: > 10 functions or > 200 instructions
  const functionCount = fnFees.size;
  let deployComplexity: 'Simple' | 'Medium' | 'Complex' = 'Simple';
  let deployFee = 2_000_000;
  if (functionCount > 10 || totalInstructionCount > 200) {
    deployComplexity = 'Complex';
    deployFee = 7_000_000;
  } else if (functionCount > 3 || totalInstructionCount > 50) {
    deployComplexity = 'Medium';
    deployFee = 4_000_000;
  }

  const sourceBytes = new TextEncoder().encode(source).length;
  const storageFee = Math.max(0, Math.ceil(sourceBytes / 1024) * FEE_CATEGORIES.StoragePerKb.cost);

  const summary: FeeSummary = {
    contract_name: contractName,
    max_fee_micro: String(Math.max(FEE_CATEGORIES.InfraIntent.cost, maxFee)),
    max_fee_display: formatUsd(Math.max(FEE_CATEGORIES.InfraIntent.cost, maxFee)),
    deploy_fee_micro: String(deployFee),
    deploy_fee_display: formatUsd(deployFee),
    storage_fee_micro: String(storageFee),
    deploy_complexity: deployComplexity,
    functions,
  };

  return { summary, lineAnnotations: annotations };
}

/**
 * Estimate fee for a single prefab action based on its intent class and operation metadata.
 */
export function estimateActionFee(action: {
  intentClass: string;
  mutates: boolean;
  parameters: { name: string }[];
}): { fee_micro: number; fee_display: string; breakdown: { label: string; cost: number }[] } {
  const breakdown: { label: string; cost: number }[] = [];
  let total = 0;

  // Base intent cost
  breakdown.push({ label: FEE_CATEGORIES.InfraIntent.label, cost: FEE_CATEGORIES.InfraIntent.cost });
  total += FEE_CATEGORIES.InfraIntent.cost;

  // Intent-class tier mapping
  switch (action.intentClass) {
    case 'MICRO':
      breakdown.push({ label: FEE_CATEGORIES.ComputeStandard.label, cost: FEE_CATEGORIES.ComputeStandard.cost });
      total += FEE_CATEGORIES.ComputeStandard.cost;
      break;
    case 'STANDARD':
    case 'MEDIUM':
      breakdown.push({ label: FEE_CATEGORIES.ComputeMedium.label, cost: FEE_CATEGORIES.ComputeMedium.cost });
      total += FEE_CATEGORIES.ComputeMedium.cost;
      break;
    case 'PREMIUM':
      breakdown.push({ label: FEE_CATEGORIES.ComputeHeavy.label, cost: FEE_CATEGORIES.ComputeHeavy.cost });
      total += FEE_CATEGORIES.ComputeHeavy.cost;
      break;
  }

  // Mutations imply state writes
  if (action.mutates) {
    breakdown.push({ label: FEE_CATEGORIES.StateLight.label, cost: FEE_CATEGORIES.StateLight.cost });
    total += FEE_CATEGORIES.StateLight.cost;
  }

  // Parameter reads (each param is a state read)
  const readCount = action.parameters.length;
  if (readCount > 0) {
    const cost = readCount * FEE_CATEGORIES.StateNano.cost;
    breakdown.push({ label: `${readCount}× ${FEE_CATEGORIES.StateNano.label}`, cost });
    total += cost;
  }

  return { fee_micro: total, fee_display: formatUsd(total), breakdown };
}

/**
 * Build a FeeSummary for a prefab from its action definitions.
 * Used when we don't have Mist source code (prefab catalog).
 */
export function estimatePrefabFees(prefab: PrefabDefinition): FeeSummary {
  const functions: FunctionFeeSummaryItem[] = [];
  let maxFee = 0;

  for (const action of prefab.actions) {
    const est = estimateActionFee(action);
    // Convert breakdown items into FunctionFeeCategoryDetail[]
    const catMap = new Map<string, { label: string; count: number; unitCost: number; total: number }>();
    for (const item of est.breakdown) {
      // Parse "N× Label" format from estimateActionFee
      const multiMatch = item.label.match(/^(\d+)×\s*(.+)$/);
      const count = multiMatch ? parseInt(multiMatch[1], 10) : 1;
      const label = multiMatch ? multiMatch[2] : item.label;
      const unitCost = count > 0 ? Math.round(item.cost / count) : item.cost;

      const existing = catMap.get(label);
      if (existing) {
        existing.count += count;
        existing.total += item.cost;
      } else {
        catMap.set(label, { label, count, unitCost, total: item.cost });
      }
    }

    const categories: FunctionFeeCategoryDetail[] = [...catMap.entries()].map(([, val]) => ({
      category: val.label,
      label: val.label,
      count: val.count,
      unit_cost: val.unitCost,
      total_cost: val.total,
      total_display: formatUsd(val.total),
    }));

    functions.push({
      function_name: action.name,
      fee_micro: String(est.fee_micro),
      fee_display: est.fee_display,
      category_count: categories.length,
      categories,
    });

    if (est.fee_micro > maxFee) maxFee = est.fee_micro;
  }

  // Deploy tier based on action count
  const actionCount = prefab.actions.length;
  let deployComplexity: 'Simple' | 'Medium' | 'Complex' = 'Simple';
  let deployFee = 2_000_000;
  if (actionCount > 10) {
    deployComplexity = 'Complex';
    deployFee = 7_000_000;
  } else if (actionCount > 3) {
    deployComplexity = 'Medium';
    deployFee = 4_000_000;
  }

  return {
    contract_name: prefab.name,
    max_fee_micro: String(Math.max(FEE_CATEGORIES.InfraIntent.cost, maxFee)),
    max_fee_display: formatUsd(Math.max(FEE_CATEGORIES.InfraIntent.cost, maxFee)),
    deploy_fee_micro: String(deployFee),
    deploy_fee_display: formatUsd(deployFee),
    storage_fee_micro: '0',
    deploy_complexity: deployComplexity,
    functions,
  };
}

/**
 * Real fee tier ranges from Rivellum tokenomics (USD micros).
 * Source: crates/rivellum-tokenomics/src/lib.rs IntentTier / FeePolicy
 */
export const FEE_TIERS = {
  MICRO:    { min: 1_000,     max: 5_000,     label: 'Micro' },
  STANDARD: { min: 10_000,    max: 50_000,    label: 'Medium' },
  MEDIUM:   { min: 10_000,    max: 50_000,    label: 'Medium' },
  PREMIUM:  { min: 100_000,   max: 500_000,   label: 'Premium' },
  DEPLOY:   { min: 2_000_000, max: 7_000_000, label: 'Deploy' },
} as const;

/**
 * Get the real fee range for an action's intent class.
 * Returns { min, max } in USD micros and formatted display strings.
 */
export function getActionFeeRange(intentClass: string): {
  min_micro: number;
  max_micro: number;
  min_display: string;
  max_display: string;
  tier_label: string;
} {
  const tier = FEE_TIERS[intentClass as keyof typeof FEE_TIERS] ?? FEE_TIERS.STANDARD;
  return {
    min_micro: tier.min,
    max_micro: tier.max,
    min_display: formatUsd(tier.min),
    max_display: formatUsd(tier.max),
    tier_label: tier.label,
  };
}

/**
 * Get the real deploy fee for a prefab based on action count (complexity tier).
 * Source: crates/rivellum-mist-spec/src/fee_engine.rs deploy tier logic
 */
export function getDeployFee(actionCount: number): {
  fee_micro: number;
  fee_display: string;
  complexity: 'Simple' | 'Medium' | 'Complex';
} {
  if (actionCount > 10) {
    return { fee_micro: 7_000_000, fee_display: '$7.0', complexity: 'Complex' };
  } else if (actionCount > 3) {
    return { fee_micro: 4_000_000, fee_display: '$4.0', complexity: 'Medium' };
  }
  return { fee_micro: 2_000_000, fee_display: '$2.0', complexity: 'Simple' };
}

function formatUsd(micro: number): string {
  const whole = Math.floor(micro / 1_000_000);
  const frac = micro % 1_000_000;
  if (frac === 0) return `$${whole}.0`;
  const fracStr = frac.toString().padStart(6, '0').replace(/0+$/, '');
  return `$${whole}.${fracStr}`;
}
